import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  input,
  OnChanges,
  OnInit,
  output,
  Renderer2,
  viewChild,
} from '@angular/core'

import * as d3 from 'd3'
import { debounceTime, filter, Subject, takeUntil, tap } from 'rxjs'

import { DestroyDirective } from '~/core/directives'
import { ISizeElement } from '~/core/models'
import {
  addInfo,
  drawDot,
  drawIsolineArea,
  drawIsolineLegend,
  drawIsolineLine,
  drawLine,
  drawXLabel,
  drawYLabel,
  forecastNumber,
  generateIsolineTooltip,
  generateIsolineTooltipValues,
} from '~/utils/index'

import { IChartContourData, IChartContourSettings } from '../../models'

import { MAX_WIDTH, TICK_SIZE } from './eps-contour-chart.constant'

@Component({
  selector: 'app-eps-contour-chart',
  template: '<div class="eps-chart" #chart></div>',
  styleUrl: './eps-contour-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [DestroyDirective],
  standalone: true,
})
export class EpsContourChartComponent implements OnInit, OnChanges {
  private readonly renderer = inject(Renderer2)

  private readonly destroy$ = inject(DestroyDirective).destroy$
  private readonly drawChart$ = new Subject()
  private readonly margin = 50
  private height = 0
  private width = 0

  readonly chartOptions = input<IChartContourSettings | null>(null)
  readonly ticker = input('')
  readonly sizeChange = output<ISizeElement>()

  readonly chartElement = viewChild<ElementRef>('chart')

  constructor() {}

  @HostListener('window:resize') onResize(): void {
    this.drawChart$.next(null)
  }

  ngOnChanges(): void {
    this.drawChart$.next(null)
  }

  ngOnInit(): void {
    this.drawChart$
      .pipe(
        debounceTime(100),
        tap(() => this.removeChart()),
        tap(() => this.setSize()),
        filter(() => Boolean(this.chartOptions()?.data)),
        tap(() => this.drawChart()),
        takeUntil(this.destroy$)
      )
      .subscribe()
    this.drawChart$.next(null)
  }

  private removeChart(): void {
    const nativeElement = this.chartElement()?.nativeElement

    nativeElement.childNodes.forEach((node: any) => this.renderer.removeChild(nativeElement, node))
  }

  private setSize(): void {
    this.width = Math.min(this.chartElement()?.nativeElement.offsetWidth, MAX_WIDTH) - this.margin
    this.height = this.width
    this.sizeChange.emit({ width: this.width, height: this.height })
  }

  private drawChart(): void {
    const marginTopContainer = this.margin
    const marginLeftContainer = this.margin
    const translates = this.chartOptions()!.translates

    const data: IChartContourData = { ...this.chartOptions()!.data }

    const chartElement = this.chartElement()

    for (const child of chartElement?.nativeElement.children) {
      this.renderer.removeChild(chartElement?.nativeElement, child)
    }

    const xMax = this.chartOptions()!.xMax
    const xMin = this.chartOptions()!.xMin
    const yMax = this.chartOptions()!.yMax
    const yMin = this.chartOptions()!.yMin

    const yInfoMax = yMax

    const y = d3.scaleLinear().domain([yMin, yMax]).range([this.height, 0])
    const x = d3.scaleLinear().domain([xMin, xMax]).range([0, this.width])

    const ticksX = Math.round(this.width / TICK_SIZE)
    const ticksY = Math.round(ticksX * 1.5)

    const yAxis = d3
      .axisLeft(y)
      .ticks(ticksY)

      .tickFormat((domainValue: any, _index: number) =>
        forecastNumber(domainValue, this.chartOptions()!.numberSettings.y.toFixed)
      )

    const xAxis = d3
      .axisBottom(x)
      .ticks(ticksX)

      .tickFormat((domainValue: any, _index: number) =>
        forecastNumber(domainValue, this.chartOptions()!.numberSettings.x.toFixed)
      )

    const makeXGridLines = (): d3.Axis<d3.NumberValue> => d3.axisBottom(x).ticks(ticksX)
    const makeYGridLines = (): d3.Axis<d3.NumberValue> => d3.axisLeft(y).ticks(ticksY)

    const area = d3
      .area()
      .curve(d3.curveBasis)

      .x((d: any) => {
        return x(d[0])
      })
      .y0(0)

      .y1((d: any) => {
        return y(d[1])
      })

    const line = d3
      .line()
      .curve(d3.curveBasis)

      .x((d: any) => {
        return x(d[0])
      })

      .y((d: any) => {
        return y(d[1])
      })

    // TODO: Fix or remove
    const { tooltip: _tooltip, tooltipTitle: _tooltipTitle } = generateIsolineTooltip(
      chartElement?.nativeElement
    )

    const svg = d3
      .select(chartElement?.nativeElement)
      .append('svg')
      .attr('height', this.height + this.margin)
      .attr('width', this.width + this.margin)
      .attr('padding-top', marginTopContainer)

    const chartGroup = svg.append('g').attr('transform', `translate(${marginLeftContainer},${2})`)

    chartGroup
      .append('g')
      .attr('class', 'grid')
      .attr('opacity', 0.2)
      .attr('transform', `translate(${0}, ${this.height})`)
      .call(
        makeXGridLines()
          .tickSize(-this.height)
          .tickFormat(() => '')
      )

    chartGroup
      .append('g')
      .attr('class', 'grid')
      .attr('opacity', 0.2)
      .call(
        makeYGridLines()
          .tickSize(-this.width)
          .tickFormat(() => '')
      )

    drawXLabel(chartGroup, this.width, this.height, translates.xLabel)
    drawYLabel(chartGroup, this.width, this.height, translates.yLabel)

    const sortedKeys = Object.keys(data).sort((a, b) => +b - +a)

    for (const key of sortedKeys) {
      const percent = +key

      drawIsolineLine({
        line,
        chartGroup,
        data: data[+key],
        strokeType: percent === 50 ? 'dash' : 'line',
        color:
          percent === 50
            ? 'var(--proximilar-chart-line-info-color)'
            : 'var(--proximilar-chart-line-color)',
      })
    }

    const lineSettings = d3
      .line()
      .x((d: any) => {
        return x(d[0])
      })
      .y((d: any) => {
        return y(d[1])
      })

    this.drawConsensusValue(chartGroup, lineSettings, xMin, yMin, x, y)
    this.drawAIValue(chartGroup, lineSettings, xMin, yMin, x, y)

    let prevXY = { x: xMin, y: yMin }

    for (const key of sortedKeys) {
      prevXY = this.drawInfo(chartGroup, x, y, prevXY, xMin, +key, data[+key])
    }

    chartGroup
      .append('g')
      .attr('class', 'axis x')
      .attr('transform', `translate(0,${this.height})`)
      .call(xAxis)

    chartGroup
      .append('g')
      .attr('class', 'axis x')
      .attr('transform', `translate(${0}, ${0})`)
      .call(yAxis)

    drawIsolineLegend({
      chartGroup,
      x,
      y,
      xPos: xMax,
      yPos: yInfoMax,
      pdfConsensus: translates.pdfConsensus || '',
      pdfForecastProximilar: translates.pdfForecastProximilar || '',
      lineExpected: translates.lineExpected || '',
    })

    this.drawTooltip({
      area,
      chartGroup,
      x,
      y,
      yMin,
      yMax,
      xMin,
      xMax,
      sortedKeys,
      data,
    })
  }

  private drawTooltip({
    area,
    chartGroup,
    x,
    y,
    yMin,
    yMax,
    xMin,
    xMax,
    sortedKeys,
    data,
  }: {
    area: d3.Line<[number, number]>
    chartGroup: d3.Selection<SVGGElement, unknown, null, undefined>
    x: d3.ScaleLinear<number, number, never>
    y: d3.ScaleLinear<number, number, never>
    yMin: number
    yMax: number
    xMin: number
    xMax: number
    sortedKeys: string[]
    data: IChartContourData
  }): void {
    const tooltipTitleText = `Only 10% probability that ${this.ticker()} results will be outside all the rings`

    const tooltipSettings = generateIsolineTooltipValues({
      el: this.chartElement()?.nativeElement,
      chartGroup,
      x,
      y,
      xMin,
      xMax,
      yMin,
      yMax,
      height: this.height,
      width: this.width,
      tooltipTitleText,
      yToFixed: this.chartOptions()!.numberSettings.y.toFixed,
      xToFixed: this.chartOptions()!.numberSettings.x.toFixed,
    })

    for (const key of sortedKeys) {
      const percent = +key
      const tooltipTitleText = `${percent}% probability that ${this.ticker()} results will be inside this ring`

      drawIsolineArea({
        area,
        chartGroup,
        data: data[+key],
        x,
        y,
        yMin,
        xMin,
        yToFixed: this.chartOptions()!.numberSettings.y.toFixed,
        xToFixed: this.chartOptions()!.numberSettings.x.toFixed,
        tooltipTitleText,
        tooltipSettings,
        opacity: 0,
      })
    }

    const { lineXValue, lineYValue } = tooltipSettings

    lineXValue.raise()
    lineYValue.raise()
  }

  private drawInfo(
    chartGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
    x: d3.ScaleLinear<number, number, never>,
    y: d3.ScaleLinear<number, number, never>,
    prevXY: { x: number; y: number },
    x1: number,
    percent: number,
    data: { x: number; y: number }[]
  ): { x: number; y: number } {
    const minValue = data.reduce(
      (minValue, current) => (minValue.y < current.y ? minValue : current),
      data[0]
    )

    // const xx = data.reduce((x, current) => Math.max(x, current.x), data[0].x)
    // const yy = (data.reduce((y, current) => current.y + y, 0)) / data.length
    const yy = data.reduce((y, current) => Math.min(y, current.y), data[0].y)
    // const x1 = 6000
    // const y1 = (prevXY.y + yy) / 2
    const y1 = yy

    addInfo({
      chartGroup,
      title: `${percent}%`,
      xVal: x(minValue.x) + 10,
      yVal: y(minValue.y),
    })

    return { x: x1, y: y1 }
  }

  private drawConsensusValue(
    chartGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
    lineSettings: d3.Line<[number, number]>,
    xMin: number,
    yMin: number,
    x: d3.ScaleLinear<number, number, never>,
    y: d3.ScaleLinear<number, number, never>
  ): void {
    const chartOptions = this.chartOptions()

    drawLine(
      chartGroup,
      lineSettings([
        [chartOptions!.revPdfConsensus, yMin],
        [chartOptions!.revPdfConsensus, chartOptions!.epsPdfConsensus],
      ]),
      'var(--proximilar-chart-info1-color)',
      2,
      0.5
    )

    drawLine(
      chartGroup,
      lineSettings([
        [xMin, chartOptions!.epsPdfConsensus],
        [chartOptions!.revPdfConsensus, chartOptions!.epsPdfConsensus],
      ]),
      'var(--proximilar-chart-info1-color)',
      2,
      0.5
    )

    drawDot(
      chartGroup,
      x(chartOptions!.revPdfConsensus),
      y(chartOptions!.epsPdfConsensus),
      'var(--proximilar-chart-info1-color)'
    )
  }

  private drawAIValue(
    chartGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
    lineSettings: d3.Line<[number, number]>,
    xMin: number,
    yMin: number,
    x: d3.ScaleLinear<number, number, never>,
    y: d3.ScaleLinear<number, number, never>
  ): void {
    const chartOptions = this.chartOptions()

    drawLine(
      chartGroup,
      lineSettings([
        [chartOptions!.revPdfForecastProximilar, yMin],
        [chartOptions!.revPdfForecastProximilar, chartOptions!.epsPdfForecastProximilar],
      ]),
      'var(--proximilar-chart-info2-color)',
      2,
      0.5
    )
    drawLine(
      chartGroup,
      lineSettings([
        [xMin, chartOptions!.epsPdfForecastProximilar],
        [chartOptions!.revPdfForecastProximilar, chartOptions!.epsPdfForecastProximilar],
      ]),
      'var(--proximilar-chart-info2-color)',
      2,
      0.5
    )
    drawDot(
      chartGroup,
      x(chartOptions!.revPdfForecastProximilar),
      y(chartOptions!.epsPdfForecastProximilar),
      'var(--proximilar-chart-info2-color)'
    )
  }
}
